import { useNavigate } from 'react-router-dom'

import { OneWaitlistDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useMarketplaceModulesPath } from 'modules/marketplace_modules/hooks/useMarketplaceModulesPath'

interface Props {
  isAiChatActive: boolean
  aiEnabled: boolean
  employeeName: string
}

const useAiPromotion = ({ isAiChatActive, aiEnabled, employeeName }: Props) => {
  const { company, canRequestUpsell } = useCurrentSession()
  const navigate = useNavigate()
  const { getListPath } = useMarketplaceModulesPath()
  const { data: oneWaitlistData, loading: oneWaitlistLoading } = useQuery(OneWaitlistDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const handleClick = () => {
    track(trackingEvents.marketplaceModules.goToModulesPage, {
      source: 'one-chat',
    })
    navigate(getListPath())
  }

  const copySets = {
    getOne: {
      title: i18n.t('growth.sidepanel_chat.waitlist.subtitle'),
      description: i18n.t('growth.sidepanel_chat.get_one.description'),
      actions: [
        {
          label: i18n.t('growth.sidepanel_chat.get_one.cta'),
          onClick: handleClick,
          buttonType: 'gradient',
          isLoading: false,
        },
      ],
    },
    hasOneInactive: {
      title: i18n.t('growth.sidepanel_chat.ai_disabled.subtitle'),
      description: i18n.t('growth.sidepanel_chat.ai_disabled.description'),
    },
  }

  const isAiAgentFeatureEnabled = !!oneWaitlistData?.features.featuresConnection.nodes[0]?.enabled
  // If client doesn't have One and is already on the waitlist
  const alreadyInOneWaitlist =
    oneWaitlistData?.companies.featureAvailabilitiesConnection.nodes[0]?.available === true &&
    !aiEnabled
  // If client doesn't have One and is not on the waitlist yet
  const canJoinOneWaitlist =
    !!oneWaitlistData?.upsell._policies.canJoinOneWaitlist.can &&
    !aiEnabled &&
    !alreadyInOneWaitlist

  const handleOpen = () => {
    track(trackingEvents.upsell.viewOneSidepanel, {
      isOneActive: isAiChatActive,
      hasOnePolicy: aiEnabled,
      isAiAgentFeatureEnabled: isAiAgentFeatureEnabled,
      alreadyInOneWaitlist: alreadyInOneWaitlist,
      canJoinOneWaitlist: canJoinOneWaitlist,
    })
  }

  const handleClose = () => {
    track(trackingEvents.upsell.closeOneSidepanel, {
      isOneActive: isAiChatActive,
      hasOnePolicy: aiEnabled,
      isAiAgentFeatureEnabled: isAiAgentFeatureEnabled,
      alreadyInOneWaitlist: alreadyInOneWaitlist,
      canJoinOneWaitlist: canJoinOneWaitlist,
    })
  }

  let selectedCopy = null

  if (aiEnabled && !isAiAgentFeatureEnabled) {
    selectedCopy = copySets.hasOneInactive
  } else {
    selectedCopy = copySets.getOne
  }

  const aiPromotionEnabled = !isAiChatActive && selectedCopy !== null && canRequestUpsell

  const aiPromotion = {
    enabled: oneWaitlistLoading ? false : aiPromotionEnabled,
    greeting: i18n.t('common.ai.greeting', { name: employeeName }),
    onShow: handleOpen,
    onHide: handleClose,
    ...selectedCopy,
  }

  return { aiPromotion, canJoinOneWaitlist }
}

export default useAiPromotion
